import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { useTranslation } from 'react-i18next';
import {
  Zap,
  Globe,
  FileText,
  Eye,
  Tag,
  UserPlus,
  Clock,
  Webhook,
} from 'lucide-react';

interface TriggerNodeData {
  label: string;
  type: string;
  icon?: string;
  config: Record<string, any>;
  isConfigured?: boolean;
}

const iconMap: Record<string, React.ReactNode> = {
  Globe: <Globe className="w-5 h-5" />,
  FileText: <FileText className="w-5 h-5" />,
  Eye: <Eye className="w-5 h-5" />,
  Tag: <Tag className="w-5 h-5" />,
  UserPlus: <UserPlus className="w-5 h-5" />,
  Clock: <Clock className="w-5 h-5" />,
  Webhook: <Webhook className="w-5 h-5" />,
};

const TriggerNode: React.FC<NodeProps<TriggerNodeData>> = ({ data, selected }) => {
  const { t } = useTranslation();

  return (
    <div
      className={`relative min-w-[180px] bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 transition-all ${
        selected
          ? 'border-emerald-500 shadow-emerald-100 dark:shadow-emerald-900/30'
          : 'border-emerald-200 dark:border-emerald-800 hover:border-emerald-400'
      }`}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-green-500 rounded-t-lg px-4 py-2">
        <div className="flex items-center gap-2 text-white">
          <Zap className="w-4 h-4" />
          <span className="text-xs font-medium uppercase tracking-wide">{t('workflow.trigger', 'Gatilho')}</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center text-emerald-600 dark:text-emerald-400">
            {iconMap[data.icon || 'Zap'] || <Zap className="w-5 h-5" />}
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900 dark:text-white text-sm leading-tight">
              {data.label}
            </p>
            {data.isConfigured ? (
              <span className="text-xs text-emerald-600 dark:text-emerald-400">{t('workflow.configured', 'Configurado')}</span>
            ) : (
              <span className="text-xs text-yellow-600 dark:text-yellow-400">{t('workflow.clickToConfigure', 'Clique para configurar')}</span>
            )}
          </div>
        </div>

        {/* Config Preview */}
        {data.config?.landingPageId && (
          <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              LP: {data.config.landingPageId}
            </p>
          </div>
        )}
      </div>

      {/* Connection Handle - Output only */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-4 !h-4 !bg-emerald-500 !border-2 !border-white dark:!border-gray-800 !rounded-full"
      />

      {/* Pulse Animation for Start Node */}
      <div className="absolute -top-1 -right-1">
        <span className="relative flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
        </span>
      </div>
    </div>
  );
};

export default memo(TriggerNode);
